import { Component, OnInit, Input, Output, EventEmitter, isDevMode, ViewChild, ElementRef, ChangeDetectorRef } from '@angular/core';
import { ComponentBase } from 't4core';
import { AppIntegrationService, AppSettingsService } from 't4-app-integration';
import { PhotoModel } from '../../Views/take-pictures/take-pictures.component';

@Component({
    selector: 'app-photo-view',
    templateUrl: './photo-view.component.html',
    styleUrls: ['./photo-view.component.css'],
    standalone: false
})
export class PhotoViewComponent extends ComponentBase implements OnInit {

  @Input() public bookingId: number;
  @Input() public event: string = "Pickup";

  @Input() public photo: PhotoModel;
  @Input() public additionalPhoto: boolean = false;

  @Output() public completed = new EventEmitter<PhotoModel>();
  @Output() public deleted = new EventEmitter<PhotoModel>();
  @Output() public failed = new EventEmitter<PhotoModel>();

  @ViewChild('video') private videoRef: ElementRef<HTMLVideoElement>;
  @ViewChild('canvas') private canvasRef: ElementRef<HTMLCanvasElement>;

  public working: boolean = false;
  public cameraOpen: boolean = false;
  public cameraLoading: boolean = false;
  public fallbackImage: string = "/Images/fallback-image-photo.png";

  private mediaStream: MediaStream = null;
  public showCameraOverlay = false;

  public videoReady: boolean =  false;

  constructor(private appService: AppIntegrationService, private appSettings: AppSettingsService, private cd: ChangeDetectorRef) { super(); }

  ngOnInit(): void {
  }

  public async startBrowserCamera(): Promise<boolean> {
    this.cameraLoading = true;
    this.cameraOpen = false;
    this.showCameraOverlay = true;
    this.videoReady = false;

    this.cd.detectChanges();
    await new Promise(res => setTimeout(res, 0));

    var started = false;

    if (this.appSettings.meetsMinimumAppVersion(4.9)) {
      started = await this.startBrowserCameraInternal();
    }

    if (!started) {
      this.showCameraOverlay = false;
      try {
        await this.takePhoto();

        if (this.photo && this.photo.taken) {
          this.cameraLoading = false;
          this.cameraOpen = false;
          return true;
        }
      } catch (ex) {
        try {
          this.Insights.trackTrace('Native camera fallback failed', {
            Exception: JSON.stringify(ex)
          });
        } catch { }
      }

      this.cameraLoading = false;
      this.cameraOpen = false;
      this.showCameraOverlay = false;
      this.failed.emit(this.photo);
      return false;
    }

    this.cameraLoading = false;
    this.cameraOpen = true;
    return true;
  }

  private async startBrowserCameraInternal(): Promise<boolean> {
    console.log("Start Browser Camera Internal tried.");

    if (!navigator.mediaDevices?.getUserMedia) {
      console.log("mediaDevices unavailable:", {
        mediaDevices: !!navigator.mediaDevices,
        getUserMedia: !!navigator.mediaDevices?.getUserMedia,
        isSecureContext: window.isSecureContext,
        protocol: location.protocol,
        userAgent: navigator.userAgent
      });
      return false;
    }

    try {
      try {
        console.log(1);
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: { ideal: 'environment' } },
          audio: false
        });
      } catch {
        console.log(2);
        this.mediaStream = await navigator.mediaDevices.getUserMedia({
          video: true,
          audio: false
        });
      }

      const videoEl = this.videoRef?.nativeElement as HTMLVideoElement | undefined;
      if (!videoEl) {
        console.log(3);
        this.Insights.trackTrace('Video element not available after opening overlay');
        return false;
      }

      videoEl.autoplay = true;
      videoEl.muted = true;
      videoEl.playsInline = true;
      videoEl.setAttribute('autoplay', '');
      videoEl.setAttribute('muted', '');
      videoEl.setAttribute('playsinline', '');
      videoEl.setAttribute('webkit-playsinline', '');

      videoEl.srcObject = this.mediaStream;

      await new Promise<void>((resolve) => {
        let done = false;
        const finish = () => {
          if (done) return;
          done = true;
          videoEl.removeEventListener('loadedmetadata', finish);
          videoEl.removeEventListener('loadeddata', finish);
          videoEl.removeEventListener('canplay', finish);
          resolve();
          console.log(4);
        };

        videoEl.addEventListener('loadedmetadata', finish, { once: true });
        videoEl.addEventListener('loadeddata', finish, { once: true });
        videoEl.addEventListener('canplay', finish, { once: true });

        setTimeout(finish, 1000);
      });
      console.log(5);
      await videoEl.play();
      this.videoReady = true;

      const track = this.mediaStream.getVideoTracks()?.[0];
      console.log("iPhone camera track:", {
        readyState: track?.readyState,
        enabled: track?.enabled,
        muted: track?.muted,
        settings: track?.getSettings?.()
      });
      console.log(6);
      this.cameraOpen = true;
      this.cameraLoading = false;
      return true;
    } catch (ex: any) {
      console.log("getUserMedia failed:", {
        name: ex?.name,
        message: ex?.message,
        constraint: ex?.constraint,
        isSecureContext: window.isSecureContext,
        protocol: location.protocol,
        userAgent: navigator.userAgent
      });
      return false;
    }
  }

  private stopBrowserCamera() {
    try {
      if (this.mediaStream) {
        this.mediaStream.getTracks().forEach(t => t.stop());
      }
    } catch (ex){
    /* ignore */
    }
    this.mediaStream = null;
    if (this.videoRef && this.videoRef.nativeElement) {
      try { this.videoRef.nativeElement.pause(); this.videoRef.nativeElement.srcObject = null; } catch {}
    }
    this.cameraOpen = false;
    this.cameraLoading = false;
    this.showCameraOverlay = false;
    this.videoReady = false;

    // ensure view updates
    try { this.cd.detectChanges(); }
    catch {
    }


  }

  private captureFromVideo(): string {
    const video = this.videoRef?.nativeElement;
    const canvas = this.canvasRef?.nativeElement;
    if (!video || !canvas) return null;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
    // Return data URL
    return canvas.toDataURL('image/jpeg', 0.85);
  }

  // Called from template when user presses "Capture" in browser camera UI
  public async captureAndUpload() {
    console.log("Capture!");
    try {
      this.working = true;
      const dataUrl = this.captureFromVideo();
      this.stopBrowserCamera();

      if (!dataUrl) {
        this.working = false;
        this.failed.emit(this.photo);
        return;
      }

      // Strip data URL header to get raw base64 payload
      const base64 = dataUrl.split(',')[1];

      this.photo.data = base64;
      this.photo.taken = true;

      // Upload to server in same way as native
      var angle = 0;
      if (this.photo.angle) angle = this.photo.angle.Id;

      try {
        console.log(base64);
        const storedUri = await this.Api.post<string>("Data/Store" + this.event + "Photo", base64, { angleId: angle, rentalId: this.bookingId });
        if (storedUri) {
          this.photo.photoUri = storedUri;
        } else {
          // fall back to embedding the captured image for preview
          this.photo.photoUri = dataUrl;
        }
      } catch (uploadEx) {
        this.Insights.trackTrace("Browser upload failed.", { Exception: JSON.stringify(uploadEx) });
        // fallback: keep the captured image locally as dataUrl so user sees it
        this.photo.photoUri = dataUrl;
      }
      console.log(this.photo);
      
      this.completed.emit(this.photo);
    } catch (ex) {
      this.Insights.trackTrace("Browser capture failed.", { Exception: JSON.stringify(ex) });
      this.failed.emit(this.photo);
    } finally {
      this.working = false;
      this.stopBrowserCamera();
    }
  }

  // Called from template when user cancels browser camera UI
  public cancelBrowserCamera() {
    this.stopBrowserCamera();
  }



  public async takePhoto() {
    // Check if demo settings is enabled for photos
    var demoMode = false;
    if (localStorage.getItem('takePhoto')) {
      var demoMode = localStorage.getItem('takePhoto') == "demo" ? true : false;
    }

    if (isDevMode() || demoMode) {
      if (!this.photo.photoUri) {
        this.working = true;
        this.photo.taken = true;
        this.photo.photoUri = "https://encrypted-tbn0.gstatic.com/images?q=tbn%3AANd9GcQpNI7ZwMcvI7BTW67q8IEVEr04ngMxps1QujZt89RjTNm46RfB&usqp=CAU";
        this.completed.emit(this.photo);
      }
      this.working = false;
    }
    else {

      if (!this.photo.photoUri) {
        this.working = true;
        var angle = 0;
        if (this.photo.angle) angle = this.photo.angle.Id;
        var uploadUri = this.Settings.getApiBase() + "Data/Store" + this.event + "Photo?angleId=" + angle + "&rentalId=" + this.bookingId;

        this.Insights.trackTrace("Taking picture");
        var p = await this.appService.takePhoto(uploadUri, this.Settings.getToken().access_token);

        if (p && p.Success) {
          this.photo.taken = true;
          try {
            this.Insights.trackTrace("Picture tooken successfully");

            if (p.Image && p.Image.length > 0) {
              this.Api.post<string>("Data/Store" + this.event + "Photo", p.Image, { angleId: angle, rentalId: this.bookingId });
              this.photo.data = p.Image;
            }

            this.completed.emit(this.photo);
          }
          catch (ex) {
            this.Insights.trackTrace("Picture upload failed.", { Exception: JSON.stringify(ex) });
            this.completed.emit(this.photo);
          }

        } else {
          if ((this.appService.Device.Camera as number) != 3)
            this.Insights.trackTrace("Picture failed - Camera not permitted");
          else
            this.Insights.trackTrace("Picture failed");

          this.failed.emit(this.photo);
        }

        this.working = false;
      }
    }
  }

  public async deletePhoto(event) {
    event.stopPropagation();

    this.photo.photoUri = null;
    this.photo.data = null;
    this.photo.taken = false;

    this.deleted.emit(this.photo);
  }
}
